"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { fetchAuthSession, signOut as amplifySignOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { api, ApiError } from "@/lib/api-client";
import { isCognitoConfigured } from "@/lib/amplify-config";
import { setStreamAuthToken, clearStreamAuthToken } from "@/hooks/queries";
import { usePathname, useRouter } from "next/navigation";

interface AuthUser {
  id: string;
  email: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  session: null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  // P6 (LEG-104a): true when the Cognito user has signed in but has
  // no firm yet (backend returned ONBOARDING_REQUIRED). Root layout /
  // protected routes use this to redirect to /onboarding.
  needsOnboarding: boolean;
  /**
   * plan-eng-review A4 2026-05-03: true once /api/me has resolved with
   * a non-empty firmClaimTypes (i.e., the user has a firm and the
   * backend is ready to serve filtered data), OR the 10-second timeout
   * fallback fired (we fail open so the page can recover with whatever
   * the backend filter sees rather than spinning forever).
   *
   * Pages that filter by firm scope (Discovery, Defendants, Cases,
   * Pipeline) should NOT issue their first fetch until isHydrated is
   * true — otherwise the request goes out with an empty filter and
   * returns zero rows even when the user has data.
   */
  isHydrated: boolean;
  /**
   * Authoritative firm claim types from /api/me. Empty array until
   * isHydrated. Used by Discovery filters and audit displays.
   */
  firmClaimTypes: string[];
}

const DEV_USER: AuthUser = {
  id: "10000000-0000-0000-0000-000000000001",
  email: "analyst@testfirm.com",
};

const DEV_AUTH_TOKEN =
  "Test 10000000-0000-0000-0000-000000000001:analyst:00000000-0000-0000-0000-000000000001";

/**
 * Hydration timeout: if /api/me hasn't resolved within this window we
 * flip isHydrated=true anyway so consumer pages stop spinning. The
 * worst case becomes "discovery fires with empty firmClaimTypes and
 * shows the empty state" rather than "discovery never renders". The
 * empty state is recoverable (refresh, retry) where an infinite
 * skeleton is not.
 */
const HYDRATION_TIMEOUT_MS = 10_000;

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  isLoading: true,
  signOut: async () => {},
  needsOnboarding: false,
  isHydrated: false,
  firmClaimTypes: [],
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [firmClaimTypes, setFirmClaimTypes] = useState<string[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);

  const syncSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      const accessToken = session.tokens?.accessToken;
      if (idToken && accessToken) {
        const tokenStr = accessToken.toString();
        api.setToken(tokenStr);
        setStreamAuthToken(tokenStr);
        setUser({
          id: idToken.payload.sub || "",
          email: (idToken.payload.email as string) || "",
        });

        // F5 (plan-eng-review 2026-04-30): /api/me is the dedicated
        // onboarding probe. Cheap (no DB hits, request.user is already
        // populated by auth middleware), semantic, and decouples the
        // onboarding probe from the dashboard endpoint. A valid Cognito
        // token without an app-side users row → 403 ONBOARDING_REQUIRED.
        //
        // plan-eng-review A4 2026-05-03: also captures firmClaimTypes
        // from the response so consumer pages (Discovery, Defendants)
        // can gate their fetches on hydration. Without this gate, the
        // first /discovery render after onboarding fires with an empty
        // filter and shows zero rows even when the firm has data.
        try {
          const me = await api.getMe();
          setNeedsOnboarding(false);
          setFirmClaimTypes(me.firmClaimTypes ?? []);
          setIsHydrated(true);
        } catch (err) {
          if (err instanceof ApiError && err.code === "ONBOARDING_REQUIRED") {
            setNeedsOnboarding(true);
            // Pre-onboarding users have no firm yet — leave
            // firmClaimTypes empty but mark hydrated so onboarding/
            // signup pages don't sit on a skeleton.
            setIsHydrated(true);
          }
          // Other errors (network, transient) leave isHydrated false;
          // the timeout fallback below will flip it after 10s so
          // consumer pages can render.
        }
      } else {
        // No tokens means unauthenticated — pages that gate on
        // isHydrated should still render their public state.
        setIsHydrated(true);
      }
    } catch {
      // Not authenticated — user will see login. Mark hydrated so the
      // login page doesn't sit waiting on a session that doesn't exist.
      setIsHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!isCognitoConfigured) {
      api.setToken(DEV_AUTH_TOKEN);
      setUser(DEV_USER);
      // Dev mode: backfill claim types from the seeded testfirm so
      // consumer pages render real data instead of waiting on a Cognito
      // probe that won't fire.
      setFirmClaimTypes([
        "data_breach",
        "antitrust",
        "financial_misselling",
        "consumer_rights",
        "employment",
        "personal_injury",
        "environmental",
      ]);
      setIsHydrated(true);
      setIsLoading(false);
      return;
    }

    syncSession().finally(() => setIsLoading(false));

    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      if (payload.event === "signedIn" || payload.event === "tokenRefresh") {
        await syncSession();
      }
      if (payload.event === "signedOut") {
        api.clearToken();
        clearStreamAuthToken();
        setUser(null);
        setFirmClaimTypes([]);
        // Keep isHydrated=true on sign-out — the user is effectively
        // hydrated (we know they have nothing). Login page renders fine.
      }
    });

    return unsubscribe;
  }, [syncSession]);

  // Hydration timeout fallback (plan-eng-review A4 2026-05-03 critical
  // gap). If /api/me hasn't responded within 10s — backend down,
  // network outage, or a stuck request — flip isHydrated=true anyway so
  // the user lands on the empty-state of the page they navigated to
  // instead of an infinite skeleton. Better UX is "Discovery shows
  // zero results, click refresh to retry" than "the page never loads
  // and the user closes the tab".
  useEffect(() => {
    if (isHydrated) return;
    const t = setTimeout(() => setIsHydrated(true), HYDRATION_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [isHydrated]);

  const signOut = useCallback(async () => {
    api.clearToken();
    clearStreamAuthToken();
    setUser(null);
    setNeedsOnboarding(false);
    if (isCognitoConfigured) {
      await amplifySignOut();
    }
    router.push("/login");
    router.refresh();
  }, [router]);

  // P6: redirect to /onboarding when needed. Skip the redirect when the
  // user is already there, on auth pages, or on the public marketing
  // landing — pre-onboarded users get to read thexis.ai/ without being
  // yanked into the wizard. Also skip /accept-invite — invited users
  // accept their invite there, which creates the users row without
  // going through the new-firm wizard.
  useEffect(() => {
    if (!needsOnboarding) return;
    if (
      pathname === "/" ||
      pathname === "/onboarding" ||
      pathname === "/login" ||
      pathname === "/signup" ||
      pathname === "/accept-invite"
    ) {
      return;
    }
    router.replace("/onboarding");
  }, [needsOnboarding, pathname, router]);

  const value = useMemo(
    () => ({
      user,
      session: null,
      isLoading,
      signOut,
      needsOnboarding,
      isHydrated,
      firmClaimTypes,
    }),
    [user, isLoading, signOut, needsOnboarding, isHydrated, firmClaimTypes],
  );

  // Render children immediately. Consumers that need to wait on auth read
  // `isLoading` from context. Previously this returned null during the
  // initial Cognito session fetch, which blanked the public marketing
  // landing for ~100-500ms after hydration.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
